/**
 * Логика поведения фиксированного баннера (banner-fixed)
 * на странице /articles/detail/index.php.
 *
 * Дисклеймер играет главенствующую роль:
 * - Пока он активен (нет классов js-static / js-inactive),
 *   баннер смещён вверх по оси Y на высоту дисклеймера.
 * - Как только дисклеймер становится неактивным (закрыт пользователем
 *   или мы доскроллили до футера), баннер опускается к самому низу экрана.
 *
 * NB: на разрешениях <=1023px у дисклеймера в стилях прописано
 * `+below(desktop) position: fixed !important;` — он визуально остаётся
 * на низу экрана даже с классом js-inactive. Это сознательное решение
 * со стороны вёрстки, баннер на этом разрешении ведёт себя по классам
 * (опускается к низу при js-inactive / js-static, как и до моих правок).
 * Соответствующая компенсация для cookies живёт в disclaimer.js.
 *
 * По оси Z плашка куки всегда выше баннера (см. cookies.styl: z-index: 11
 * и banner-fixed.styl: z-index: 1), поэтому при пересечении
 * куки частично (десктоп) или полностью (мобильный) перекрывает баннер.
 *
 * Координаты пересчитываются:
 * - при скролле документа (на случай, если дисклеймер сменил состояние
 *   из-за приближения к футеру),
 * - при ресайзе окна (вместе с пересчётом высоты дисклеймера),
 * - при изменении классов / inline-стилей дисклеймера через MutationObserver
 *   (чтобы поймать момент закрытия дисклеймера крестиком / кнопкой подтверждения,
 *   когда событий scroll / resize не происходит).
 *
 * У баннера есть кнопка закрытия (.js-banner-close),
 * по клику на которую баннер скрывается, а все слушатели снимаются.
 */
itsoftApp.createModule("bannerFixed", ["pageLoad", "disclaimer", "cookie"], function () {
    const module = this;

    const BannerFixed = function () {
        this.banner = document.querySelector(".js-banner-root");
        this.disclaimer = document.querySelector(".js-disclaimer");
        this.closeButton = null;
        this.observer = null;
        this.gap = 0; /* Зазор между низом дисклеймера и низом баннера. По аналогии с cookies/disclaimer. */
        this.disclaimerHeight = 0;
        this.isClosed = false;

        if (this.banner) {
            this.closeButton = this.banner.querySelector(".js-banner-close");

            this.onScroll = this.updatePosition.bind(this);
            this.onResize = this.onDocumentResize.bind(this);

            this.init();
        }
    };

    /**
     * Баннер ориентируется на классы дисклеймера ровно так же, как раньше делали cookies:
     * нет js-static и нет js-inactive — дисклеймер активен, баннер приподнят.
     *
     * На разрешениях <=1023px дисклеймер визуально остаётся фиксированным даже
     * при js-inactive (см. CSS `+below(desktop) position: fixed !important;`),
     * но это намеренное решение вёрстки: баннер на мобилке опускается к низу,
     * cookies же остаются выше — компенсация для cookies сделана в disclaimer.js.
     */
    BannerFixed.prototype.isDisclaimerActive = function () {
        if (!this.disclaimer) {
            return false;
        }

        return !this.disclaimer.classList.contains("js-static") &&
               !this.disclaimer.classList.contains("js-inactive");
    };

    /**
     * Берём актуальную высоту дисклеймера.
     * Намеренно НЕ полагаемся на itsoftApp.calculations.disclaimerHeight:
     * этот кэш заполняется в disclaimer.js уже после первого checkElementCoords,
     * поэтому до первого скролла кэш может оказаться null и сломать позиционирование.
     * getBoundingClientRect всегда возвращает реальную высоту отрисованного дисклеймера.
     */
    BannerFixed.prototype.getDisclaimerHeight = function () {
        if (!this.disclaimer) {
            return 0;
        }

        return Math.round(this.disclaimer.getBoundingClientRect().height);
    };

    /**
     * Основной метод — выставляет нижнюю координату баннера в зависимости от состояния дисклеймера.
     * Используется и при инициализации, и при скролле, и при ресайзе,
     * и при изменении классов дисклеймера.
     */
    BannerFixed.prototype.updatePosition = function () {
        if (!this.banner || this.isClosed) {
            return;
        }

        if (this.isDisclaimerActive()) {
            this.disclaimerHeight = this.getDisclaimerHeight();
            this.banner.style.bottom = (this.disclaimerHeight + this.gap) + "px";
        } else {
            /* Дисклеймер неактивен — прижимаем баннер к низу экрана,
               как это делают cookies в disclaimer.js (DISCLAIMER.cookies.style.bottom = 0). */
            this.banner.style.bottom = "0px";
        }
    };

    /**
     * При ресайзе сначала пересчитываем кэшированную высоту дисклеймера,
     * затем уже двигаем баннер. Это страхует от ситуаций,
     * когда наш resize-обработчик отрабатывает раньше windowResize-контроллера.
     */
    BannerFixed.prototype.onDocumentResize = function () {
        if (this.disclaimer && itsoftApp.utils && typeof itsoftApp.utils.getDisclaimerHeight === "function") {
            itsoftApp.utils.getDisclaimerHeight();
        }

        this.updatePosition();
    };

    /**
     * Закрытие баннера по клику на крестик.
     * Скрываем баннер и снимаем все слушатели, чтобы не молотить вхолостую.
     */
    BannerFixed.prototype.close = function () {
        const BANNER = this;

        BANNER.isClosed = true;

        if (BANNER.banner) {
            BANNER.banner.classList.add("is-hidden");
            BANNER.banner.style.display = "none";
        }

        document.removeEventListener("scroll", BANNER.onScroll);
        window.removeEventListener("resize", BANNER.onResize);

        if (BANNER.observer) {
            BANNER.observer.disconnect();
            BANNER.observer = null;
        }
    };

    BannerFixed.prototype.bindCloseButton = function () {
        const BANNER = this;

        if (!BANNER.closeButton) {
            return;
        }

        BANNER.closeButton.addEventListener("click", function (evt) {
            evt.preventDefault();
            BANNER.close();
        });
    };

    /**
     * Дисклеймер может менять состояние не только при скролле/ресайзе,
     * но и при клике на кнопку закрытия / подтверждения внутри disclaimer.js
     * (тогда добавляются классы js-static / js-inactive, и cookie.bottom = 0).
     * Чтобы баннер мгновенно реагировал на это, наблюдаем за изменением classList и style.
     */
    BannerFixed.prototype.observeDisclaimer = function () {
        const BANNER = this;

        if (!BANNER.disclaimer || typeof MutationObserver === "undefined") {
            return;
        }

        BANNER.observer = new MutationObserver(function () {
            BANNER.updatePosition();
        });

        BANNER.observer.observe(BANNER.disclaimer, {
            attributes: true,
            attributeFilter: ["class", "style"]
        });
    };

    BannerFixed.prototype.init = function () {
        const BANNER = this;

        BANNER.bindCloseButton();

        /* Инициализируем модуль после полной загрузки страницы,
           чтобы дисклеймер уже был отрендерен и его высоту можно было корректно измерить. */
        itsoftApp.modules.pageLoad.addEvent(function () {
            /* Первый запуск может прийтись на момент,
               когда дисклеймер ещё дорисовывает себя
               (например, переход из js-inactive в active),
               поэтому повторяем расчёт на следующем кадре. */
            BANNER.updatePosition();

            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(function () {
                    BANNER.updatePosition();
                });
            }

            document.addEventListener("scroll", BANNER.onScroll);
            window.addEventListener("resize", BANNER.onResize);

            BANNER.observeDisclaimer();
        });
    };

    window.BANNER_FIXED = new BannerFixed();
    module.callback = BannerFixed;
});
